import { Application, Container, Graphics, Text } from "pixi.js";
import { GameState, Bottle, Phase } from "../core/types";
import { GameStage } from "./pixi/stage";
import { LayoutManager, createLayoutManager } from "./pixi/layout";
import { COLORS, getWaterColor } from "./theme/colors";

type ButtonCallback = () => void;
type ButtonState = "normal" | "hover" | "pressed" | "disabled";

type ButtonContainer = Container & {
  setState?: (state: ButtonState) => void;
  currentState?: ButtonState;
};

const TITLE_FONT = "Trebuchet MS, Verdana, sans-serif";
const UI_FONT = "Trebuchet MS, Verdana, sans-serif";

export const PACK_ANIMATION = {
  delayMs: 150,
  durationMs: 800
};

const mixColor = (a: number, b: number, t: number): number => {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const rr = Math.round(ar + (br - ar) * t);
  const rg = Math.round(ag + (bg - ag) * t);
  const rb = Math.round(ab + (bb - ab) * t);
  return (rr << 16) + (rg << 8) + rb;
};

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export class Renderer {
  private app: Application;
  private stage: GameStage;
  private layout: LayoutManager;
  private bottleViews: Map<number, Container> = new Map();
  private slotViews: Map<number, Container> = new Map();
  private hudContainer: Container;
  private buttonsContainer: Container;
  private headerContainer: Container;
  private winContainer: Container | null = null;
  private selectedBottleId: number | null = null;
  private buttonCallbacks: Map<string, ButtonCallback> = new Map();

  constructor(app: Application) {
    this.app = app;
    this.stage = new GameStage(app, {
      width: app.screen.width,
      height: app.screen.height
    });
    this.layout = createLayoutManager({
      width: app.screen.width,
      height: app.screen.height
    });
    this.hudContainer = new Container();
    this.buttonsContainer = new Container();
    this.headerContainer = new Container();
    this.renderBackground();
    this.setupHUD();
    this.setupButtons();
  }

  setButtonCallback(name: string, callback: ButtonCallback): void {
    this.buttonCallbacks.set(name, callback);
  }

  private setupHUD(): void {
    const uiLayer = this.stage.getLayer("ui");
    uiLayer.addChild(this.hudContainer);
    uiLayer.addChild(this.headerContainer);
  }

  private setupButtons(): void {
    this.rebuildButtons();
  }

  private rebuildButtons(): void {
    const uiLayer = this.stage.getLayer("ui");
    if (!uiLayer.children.includes(this.buttonsContainer)) {
      uiLayer.addChild(this.buttonsContainer);
    }
    for (let i = this.buttonsContainer.children.length - 1; i >= 0; i--) {
      this.buttonsContainer.children[i].destroy();
    }

    const config = this.layout.getConfig();
    const isMobile = config.isMobile;
    
    // Use layout positions for buttons
    const restartPos = this.layout.getButtonPosition(0);
    const nextPos = this.layout.getButtonPosition(1);

    // Restart button
    this.createButton(isMobile ? "重开" : "Restart", restartPos.x + restartPos.width / 2, restartPos.y + restartPos.height / 2, () => {
      this.buttonCallbacks.get("restart")?.();
    }, restartPos.width, restartPos.height);

    // Next Level button (hidden by default)
    const nextLabel = isMobile ? "下一关" : "Next Level";
    const nextBtn = this.createButton(nextLabel, nextPos.x + nextPos.width / 2, nextPos.y + nextPos.height / 2, () => {
      this.buttonCallbacks.get("nextLevel")?.();
    }, nextPos.width, nextPos.height);
    nextBtn.name = "nextLevelBtn";
    const btn = nextBtn as ButtonContainer;
    btn.setState?.("disabled");
    btn.alpha = 0.55;
    btn.eventMode = "none";
  }

  private createButton(label: string, x: number, y: number, onClick: () => void, width?: number, height?: number): Container {
    const container = new Container() as ButtonContainer;
    container.x = x;
    container.y = y;
    container.eventMode = "static";
    container.cursor = "pointer";

    const config = this.layout.getConfig();
    const btnWidth = width ?? 100;
    const btnHeight = height ?? 40;
    const fontSize = config.isSmallMobile ? 11 : config.isMobile ? 13 : 15;
    const cornerRadius = config.isMobile ? 14 : 18;

    const glow = new Graphics();
    const bg = new Graphics();
    const shine = new Graphics();
    const border = new Graphics();

    const text = new Text({
      text: label,
      style: {
        fontFamily: UI_FONT,
        fontSize: fontSize,
        fill: COLORS.ui.text.primary,
        align: "center"
      }
    });
    text.anchor.set(0.5);

    container.addChild(glow);
    container.addChild(bg);
    container.addChild(shine);
    container.addChild(border);
    container.addChild(text);

    const drawButton = (state: ButtonState) => {
      const isDisabled = state === "disabled";
      const baseColor = isDisabled ? COLORS.ui.button.disabled
        : state === "pressed" ? COLORS.ui.button.pressed
        : state === "hover" ? COLORS.ui.button.hover
        : COLORS.ui.button.background;
      const glowColor = COLORS.ui.button.glow ?? COLORS.ui.button.hover;

      glow.clear();
      bg.clear();
      shine.clear();
      border.clear();

      glow.beginFill(glowColor, isDisabled ? 0.15 : 0.35);
      glow.drawRoundedRect(-btnWidth / 2 - 6, -btnHeight / 2 - 6, btnWidth + 12, btnHeight + 12, cornerRadius + 8);
      glow.endFill();

      bg.beginFill(baseColor, 0.95);
      bg.drawRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, cornerRadius);
      bg.endFill();

      shine.beginFill(0xffffff, isDisabled ? 0.1 : 0.25);
      shine.drawRoundedRect(-btnWidth / 2 + 6, -btnHeight / 2 + 4, btnWidth - 12, btnHeight * 0.45, cornerRadius);
      shine.endFill();

      border.lineStyle(2, COLORS.ui.text.primary, isDisabled ? 0.2 : 0.4);
      border.drawRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, cornerRadius);
      border.stroke();

      text.alpha = isDisabled ? 0.6 : 1;
    };

    container.setState = (state: ButtonState) => {
      container.currentState = state;
      drawButton(state);
    };
    container.setState("normal");

    container.on("pointertap", () => {
      if (container.currentState === "disabled") return;
      onClick();
    });

    // Hover effect
    container.on("pointerover", () => {
      if (container.currentState === "disabled") return;
      container.setState?.("hover");
    });
    container.on("pointerout", () => {
      if (container.currentState === "disabled") return;
      container.setState?.("normal");
    });
    container.on("pointerdown", () => {
      if (container.currentState === "disabled") return;
      container.setState?.("pressed");
    });
    container.on("pointerup", () => {
      if (container.currentState === "disabled") return;
      container.setState?.("hover");
    });

    this.buttonsContainer.addChild(container);
    return container;
  }

  render(state: GameState, selectedBottleId: number | null = null, gamePhase: Phase = "idle") {
    this.selectedBottleId = selectedBottleId;

    this.clearGameLayer("bottles");
    this.clearGameLayer("slots");

    this.renderHeader(state);
    this.renderSlots(state);
    this.renderBottles(state);

    // Update Next Level button state
    const nextBtn = this.buttonsContainer.getChildByName("nextLevelBtn") as ButtonContainer | null;
    if (nextBtn) {
      if (gamePhase === "win") {
        nextBtn.alpha = 1;
        nextBtn.eventMode = "static";
        nextBtn.cursor = "pointer";
        nextBtn.setState?.("normal");
      } else {
        nextBtn.alpha = 0.55;
        nextBtn.eventMode = "none";
        nextBtn.setState?.("disabled");
      }
    }

    // Show/hide win screen
    if (gamePhase === "win" && !this.winContainer) {
      this.showWinScreen(state);
    } else if (gamePhase !== "win" && this.winContainer) {
      this.hideWinScreen();
    }
  }

  private clearGameLayer(layerName: string): void {
    const layer = this.stage.getLayer(layerName);
    for (let i = layer.children.length - 1; i >= 0; i--) {
      layer.children[i].destroy();
    }
  }

  private renderBackground(): void {
    const layer = this.stage.getLayer("background");
    for (let i = layer.children.length - 1; i >= 0; i--) {
      layer.children[i].destroy();
    }

    const width = this.app.screen.width;
    const height = this.app.screen.height;
    const background = new Graphics();

    background.beginFill(COLORS.ocean.deep);
    background.drawRect(0, 0, width, height);
    background.endFill();

    const steps = 10;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const color = mixColor(COLORS.ocean.mid, COLORS.ocean.light, t);
      const y = (height / steps) * i;
      background.beginFill(color, 0.18);
      background.drawRect(0, y, width, height / steps + 2);
      background.endFill();
    }

    layer.addChild(background);

    const rayCount = 6;
    for (let i = 0; i < rayCount; i++) {
      const ray = new Graphics();
      const offset = (i - rayCount / 2) * (width * 0.08);
      ray.beginFill(COLORS.ocean.glow, 0.08);
      ray.moveTo(width * 0.5 + offset, 0);
      ray.lineTo(width * 0.5 + offset + width * 0.14, 0);
      ray.lineTo(width * 0.5 + offset + width * 0.32, height * 0.8);
      ray.lineTo(width * 0.5 + offset + width * 0.1, height * 0.8);
      ray.closePath();
      ray.endFill();
      layer.addChild(ray);
    }

    const seabed = new Graphics();
    const seabedTop = height * 0.78;
    seabed.beginFill(COLORS.ocean.seabed, 0.9);
    seabed.moveTo(0, height);
    const waveStep = width / 6;
    for (let x = 0; x <= width + waveStep; x += waveStep) {
      const wave = Math.sin((x / width) * Math.PI * 2) * 18;
      seabed.lineTo(x, seabedTop + wave);
    }
    seabed.lineTo(width, height);
    seabed.closePath();
    seabed.endFill();
    layer.addChild(seabed);

    const bubbles = new Graphics();
    const bubbleCount = 20;
    for (let i = 0; i < bubbleCount; i++) {
      const radius = 2 + (i % 5);
      const x = (i * 97) % width;
      const y = (i * 53) % (height * 0.75);
      bubbles.lineStyle(1, COLORS.ocean.glow, 0.25);
      bubbles.drawCircle(x, y + 20, radius);
    }
    layer.addChild(bubbles);
  }

  private renderBottles(state: GameState): void {
    const bottleLayer = this.stage.getLayer("bottles");
    this.bottleViews.clear();
    const { offsetX, offsetY } = this.layout.getBottleVisualMetrics();

    state.bottles.forEach((bottle, bottleIndex) => {
      const pos = this.layout.getBottlePosition(bottleIndex, state.bottles.length, state.packing.slots.length);
      const bottleContainer = this.createBottleView(bottle);
      bottleContainer.x = pos.x + offsetX;
      bottleContainer.y = pos.y + offsetY;
      bottleLayer.addChild(bottleContainer);
      this.bottleViews.set(bottle.id, bottleContainer);
    });
  }

  private createBottleView(bottle: Bottle): Container {
    const container = new Container();
    container.eventMode = "static";
    container.cursor = "pointer";
    container.name = `bottle-${bottle.id}`;

    const { width, height } = this.layout.getBottleVisualMetrics();
    const layerHeight = height / 5;

    const graphics = new Graphics();

    const isEmpty = bottle.layers.length === 0;

    // Draw bottle background (glass) - keep minimal tint to avoid affecting water color
    const glassAlpha = isEmpty ? 0.03 : 0;
    if (glassAlpha > 0) {
      graphics.beginFill(COLORS.bottle.glass, glassAlpha);
      graphics.drawRoundedRect(0, 0, width, height, 10);
      graphics.endFill();
    }

    // Draw liquid layers (from bottom to top), merge adjacent same colors
    let runColor: (typeof bottle.layers)[number] | null = null;
    let runStart = 0;
    const layers = bottle.layers;

    for (let i = 0; i <= layers.length; i++) {
      const color = i < layers.length ? layers[i] : null;
      if (i === 0) {
        runColor = color;
        runStart = 0;
        continue;
      }

      if (color === runColor && i < layers.length) {
        continue;
      }

      if (runColor) {
        const runLength = i - runStart;
        const y = height - (runStart + runLength) * layerHeight;
        const runHeight = runLength * layerHeight - 1;
        const baseColor = getWaterColor(runColor);
        graphics.lineStyle(1, mixColor(baseColor, 0x000000, 0.15), 0.6);
        graphics.beginFill(baseColor, 1);
        graphics.drawRoundedRect(2, y, width - 4, runHeight, 6);
        graphics.endFill();
        graphics.lineStyle(0);
      }

      runColor = color;
      runStart = i;
    }

    // Draw bottle outline
    graphics.lineStyle(2, COLORS.bottle.rim, isEmpty ? 0.25 : 0.45);
    graphics.drawRoundedRect(0, 0, width, height, 10);

    // Draw bottle cap/neck at top
    const neckHeight = Math.max(6, Math.floor(height * 0.06));
    const neckWidth = Math.max(10, Math.floor(width * 0.5));
    graphics.beginFill(COLORS.bottle.rim, isEmpty ? 0.25 : 0.5);
    graphics.drawRoundedRect(
      (width - neckWidth) / 2,
      -neckHeight - 2,
      neckWidth,
      neckHeight,
      6
    );
    graphics.endFill();

    // Draw selection indicator - make it more prominent
    if (this.selectedBottleId === bottle.id) {
      // Glow effect
      graphics.beginFill(COLORS.ocean.glow, 0.25);
      graphics.drawRoundedRect(-8, -8, width + 16, height + 16, 14);
      graphics.endFill();
      
      // Border
      graphics.lineStyle(3, COLORS.ui.text.accent, 1);
      graphics.drawRoundedRect(-6, -6, width + 12, height + 12, 12);
      
      // Inner highlight
      graphics.lineStyle(2, 0xffffff, 0.6);
      graphics.drawRoundedRect(-3, -3, width + 6, height + 6, 10);
    }

    // Draw seal indicator
    if (bottle.sealed) {
      graphics.beginFill(0x000000, 0.5);
      graphics.drawRoundedRect(0, 0, width, height, 5);
      graphics.endFill();

      const sealText = new Text({
        text: "SEALED",
        style: {
          fontFamily: UI_FONT,
          fontSize: 10,
          fill: COLORS.ui.text.primary,
          align: "center"
        }
      });
      sealText.anchor.set(0.5);
      sealText.x = width / 2;
      sealText.y = height / 2;
      container.addChild(sealText);
    }

    container.addChild(graphics);

    return container;
  }

  private renderSlots(state: GameState): void {
    const slotLayer = this.stage.getLayer("slots");
    this.slotViews.clear();
    
    const config = this.layout.getConfig();
    const slotWidth = config.slotWidth - 10;
    const slotHeight = config.slotHeight - 10;
    
    state.packing.slots.forEach((color, index) => {
      const slotContainer = new Container();
      const pos = this.layout.getSlotPosition(index, state.packing.slots.length);
      slotContainer.x = pos.x + 5;
      slotContainer.y = pos.y + 5;

      const graphics = new Graphics();

      const slotColor = color ? getWaterColor(color) : null;
      const fillColor = slotColor ?? COLORS.slot.background;
      const corner = Math.max(6, slotWidth * 0.12);
      const borderColor = slotColor ? mixColor(fillColor, 0x000000, 0.2) : COLORS.slot.border;
      const lidColor = slotColor ? mixColor(fillColor, 0xffffff, 0.2) : mixColor(fillColor, 0xffffff, 0.08);
      const baseColor = slotColor ? mixColor(fillColor, 0x000000, 0.12) : mixColor(fillColor, 0x000000, 0.2);
      const latchColor = slotColor ? mixColor(fillColor, 0x000000, 0.35) : COLORS.slot.border;
      const lidHeight = Math.max(10, Math.floor(slotHeight * 0.3));

      // Box body
      graphics.beginFill(baseColor, color ? 1 : 0.55);
      graphics.drawRoundedRect(0, lidHeight - 2, slotWidth, slotHeight - lidHeight + 2, corner);
      graphics.endFill();

      // Lid
      graphics.beginFill(lidColor, color ? 1 : 0.55);
      graphics.drawRoundedRect(0, 0, slotWidth, lidHeight, corner);
      graphics.endFill();

      // Inner rim highlight
      graphics.lineStyle(1, mixColor(fillColor, 0xffffff, 0.35), color ? 0.8 : 0.25);
      graphics.drawRoundedRect(3, 3, slotWidth - 6, slotHeight - 6, Math.max(4, corner - 3));
      graphics.stroke();

      // Latch
      const latchWidth = Math.max(10, Math.floor(slotWidth * 0.22));
      const latchHeight = Math.max(8, Math.floor(slotHeight * 0.16));
      graphics.beginFill(latchColor, color ? 1 : 0.5);
      graphics.drawRoundedRect(
        slotWidth / 2 - latchWidth / 2,
        lidHeight - latchHeight / 2,
        latchWidth,
        latchHeight,
        4
      );
      graphics.endFill();

      // Outer border
      graphics.lineStyle(2, borderColor, color ? 1 : 0.4);
      graphics.drawRoundedRect(0, 0, slotWidth, slotHeight, corner);
      graphics.stroke();

      if (color) {
        const fontSize = config.isSmallMobile ? 16 : config.isMobile ? 20 : 24;
        const colorText = new Text({
          text: color.charAt(0).toUpperCase(),
          style: {
            fontFamily: UI_FONT,
            fontSize: fontSize,
            fill: COLORS.ui.text.primary,
            align: "center",
            fontWeight: "bold",
            dropShadow: {
              color: COLORS.ocean.glow,
              blur: 6,
              distance: 0,
              angle: Math.PI / 2,
              alpha: 0.7
            }
          }
        });
        colorText.anchor.set(0.5);
        colorText.x = slotWidth / 2;
        colorText.y = slotHeight * 0.6;
        slotContainer.addChild(colorText);
      }

      slotContainer.addChild(graphics);
      slotLayer.addChild(slotContainer);
      this.slotViews.set(index, slotContainer);
    });
  }

  private renderHeader(state: GameState): void {
    for (let i = this.headerContainer.children.length - 1; i >= 0; i--) {
      this.headerContainer.children[i].destroy();
    }

    const config = this.layout.getConfig();
    const padding = config.padding;
    const headerHeight = config.headerHeight;

    const title = new Text({
      text: "UNDERWATER",
      style: {
        fontFamily: TITLE_FONT,
        fontSize: config.headerTitleSize,
        fill: COLORS.ui.text.primary,
        fontWeight: "bold",
        dropShadow: {
          color: COLORS.ocean.glow,
          blur: 10,
          distance: 0,
          angle: Math.PI / 2,
          alpha: 0.8
        }
      }
    });
    title.anchor.set(0.5);
    title.x = this.app.screen.width / 2;
    title.y = padding + headerHeight * 0.35;
    this.headerContainer.addChild(title);

    const subtitle = new Text({
      text: "WATER SORT ADVENTURE",
      style: {
        fontFamily: UI_FONT,
        fontSize: Math.max(12, Math.floor(config.headerInfoSize * 1.1)),
        fill: COLORS.ui.text.accent,
        fontWeight: "bold",
        dropShadow: {
          color: COLORS.ocean.glow,
          blur: 6,
          distance: 0,
          angle: Math.PI / 2,
          alpha: 0.7
        }
      }
    });
    subtitle.anchor.set(0.5);
    subtitle.x = this.app.screen.width / 2;
    subtitle.y = padding + headerHeight * 0.75;
    this.headerContainer.addChild(subtitle);

    const hiddenInfo = state.hiddenBottles.length > 0 ? ` +${state.hiddenBottles.length}` : "";
    const infoText = new Text({
      text: `L${state.level} | S:${state.score} | M:${state.moves.length}${hiddenInfo}`,
      style: {
        fontFamily: UI_FONT,
        fontSize: config.headerInfoSize,
        fill: COLORS.ui.text.secondary,
        dropShadow: {
          color: COLORS.ocean.glow,
          blur: 6,
          distance: 0,
          angle: Math.PI / 2,
          alpha: 0.6
        }
      }
    });
    infoText.anchor.set(1, 0.5);
    infoText.x = this.app.screen.width - padding;
    infoText.y = padding + headerHeight * 0.8;
    this.headerContainer.addChild(infoText);
  }

  private showWinScreen(state: GameState): void {
    const overlayLayer = this.stage.getLayer("overlays");
    this.winContainer = new Container();

    // Background overlay
    const bg = new Graphics();
    bg.beginFill(0x000000, 0.75);
    bg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    bg.endFill();
    bg.eventMode = "static";
    this.winContainer.addChild(bg);

    // Win text
    const winText = new Text({
      text: "LEVEL COMPLETE!",
      style: {
        fontFamily: TITLE_FONT,
        fontSize: 40,
        fill: COLORS.ui.text.accent,
        align: "center",
        fontWeight: "bold",
        dropShadow: {
          color: COLORS.ocean.glow,
          blur: 10,
          distance: 0,
          angle: Math.PI / 2,
          alpha: 0.8
        }
      }
    });
    winText.anchor.set(0.5);
    winText.x = this.app.screen.width / 2;
    winText.y = this.app.screen.height / 2 - 60;
    this.winContainer.addChild(winText);

    // Score text
    const scoreText = new Text({
      text: `Score: ${state.score}  |  Moves: ${state.moves.length}`,
      style: {
        fontFamily: UI_FONT,
        fontSize: 24,
        fill: COLORS.ui.text.primary,
        align: "center"
      }
    });
    scoreText.anchor.set(0.5);
    scoreText.x = this.app.screen.width / 2;
    scoreText.y = this.app.screen.height / 2;
    this.winContainer.addChild(scoreText);

    // Continue button
    const continueBtn = this.createWinButton("Continue", this.app.screen.height / 2 + 60, () => {
      this.buttonCallbacks.get("nextLevel")?.();
    });
    this.winContainer.addChild(continueBtn);

    overlayLayer.addChild(this.winContainer);
  }

  private hideWinScreen(): void {
    if (this.winContainer) {
      this.winContainer.destroy();
      this.winContainer = null;
    }
  }

  private createWinButton(label: string, y: number, onClick: () => void): Container {
    const container = new Container();
    container.x = this.app.screen.width / 2;
    container.y = y;
    container.eventMode = "static";
    container.cursor = "pointer";

    const width = 150;
    const height = 50;

    const bg = new Graphics();
    bg.beginFill(COLORS.ui.button.background);
    bg.drawRoundedRect(-width / 2, -height / 2, width, height, 18);
    bg.endFill();

    const shine = new Graphics();
    shine.beginFill(0xffffff, 0.25);
    shine.drawRoundedRect(-width / 2 + 8, -height / 2 + 6, width - 16, height * 0.4, 14);
    shine.endFill();

    const text = new Text({
      text: label,
      style: {
        fontFamily: UI_FONT,
        fontSize: 18,
        fill: COLORS.ui.text.primary,
        align: "center"
      }
    });
    text.anchor.set(0.5);

    container.addChild(bg);
    container.addChild(shine);
    container.addChild(text);

    container.on("pointertap", onClick);

    container.on("pointerover", () => {
      bg.clear();
      bg.beginFill(COLORS.ui.button.hover);
      bg.drawRoundedRect(-width / 2, -height / 2, width, height, 18);
      bg.endFill();
    });
    container.on("pointerout", () => {
      bg.clear();
      bg.beginFill(COLORS.ui.button.background);
      bg.drawRoundedRect(-width / 2, -height / 2, width, height, 18);
      bg.endFill();
    });

    return container;
  }

  resize(width: number, height: number): void {
    this.layout.updateDimensions(width, height);
    this.stage.resize(width, height);
    this.renderBackground();
    this.rebuildButtons();
  }

  destroy(): void {
    this.stage.destroy();
    this.bottleViews.clear();
    this.slotViews.clear();
  }

  // Animation methods - simplified to avoid container issues
  private animateWithCheck(
    id: number,
    type: "bottle" | "slot",
    update: (target: Container) => boolean,
    onComplete?: () => void
  ): void {
    const getTarget = () => type === "bottle" 
      ? this.bottleViews.get(id) 
      : this.slotViews.get(id);
    
    let target = getTarget();
    if (!target) {
      onComplete?.();
      return;
    }

    const frame = () => {
      // Re-fetch target as it may have been destroyed
      target = getTarget();
      if (!target) {
        onComplete?.();
        return;
      }
      
      const done = update(target);
      if (!done) {
        requestAnimationFrame(frame);
      } else {
        onComplete?.();
      }
    };

    requestAnimationFrame(frame);
  }

  animatePackAway(bottleId: number, slotIndex?: number, onComplete?: () => void): void {
    const startTarget = this.bottleViews.get(bottleId);
    if (!startTarget) {
      onComplete?.();
      return;
    }

    const config = this.layout.getConfig();
    const { width: bottleWidth, height: bottleHeight } = this.layout.getBottleVisualMetrics();
    const slotWidth = config.slotWidth - 10;
    const slotHeight = config.slotHeight - 10;

    const startX = startTarget.x;
    const startY = startTarget.y;
    const startScale = startTarget.scale.x || 1;

    let targetX = startX;
    let targetY = startY;

    if (slotIndex !== undefined) {
      const slot = this.slotViews.get(slotIndex);
      if (slot) {
        targetX = slot.x + (slotWidth - bottleWidth) / 2;
        targetY = slot.y + (slotHeight - bottleHeight) / 2;
      } else {
        const pos = this.layout.getSlotPosition(slotIndex, this.slotViews.size || 1);
        targetX = pos.x + 5 + (slotWidth - bottleWidth) / 2;
        targetY = pos.y + 5 + (slotHeight - bottleHeight) / 2;
      }
    }

    const duration = PACK_ANIMATION.durationMs;
    const startTime = performance.now();

    const frame = (now: number) => {
      const target = this.bottleViews.get(bottleId);
      if (!target) {
        onComplete?.();
        return;
      }

      const t = Math.min(1, (now - startTime) / duration);
      const eased = easeInOutCubic(t);
      target.x = startX + (targetX - startX) * eased;
      target.y = startY + (targetY - startY) * eased;
      const scale = startScale * (1 - 0.65 * eased);
      target.scale.set(scale);
      target.alpha = 1 - 0.9 * eased;

      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        target.alpha = 0;
        onComplete?.();
      }
    };

    requestAnimationFrame(frame);
  }

  flashSlot(slotIndex: number): void {
    let flashes = 0;
    let bright = true;
    
    this.animateWithCheck(
      slotIndex,
      "slot",
      (target) => {
        flashes++;
        
        if (flashes % 3 === 0) {
          bright = !bright;
          target.alpha = bright ? 1 : 0.3;
        }
        
        if (flashes >= 20) {
          target.alpha = 1;
          return true; // done
        }
        return false; // continue
      }
    );
  }
}
